import { Alert, Heading } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { getDirtyValues } from "@eisox/tools";
import { ParaglideMessage } from "@inlang/paraglide-js-react";
import { useSelector } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";

import {
  DEFAULT_FREE_COOLING_DELTA,
  MAXIMUM_FREE_COOLING_DELTA,
  MINIMUM_FREE_COOLING_DELTA,
  STEP,
} from "~/constants/air-handler-unit.constant";
import {
  DiscrepancyLabel,
  Inconsistency,
  useAirHandlerUnitContext,
  useUpdateAirHandlerUnit,
} from "~/features/air-handler-unit";
import { m } from "~/paraglide/messages";

export const Route = createFileRoute(
  "/_authenticated/buildings_/$buildingId/air-handler-units/$airHandlerUnitId/settings/free-cooling",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { buildingId } = Route.useParams();
  const { airHandlerUnit } = useAirHandlerUnitContext(Route.id);

  const { mutateAsync } = useUpdateAirHandlerUnit(buildingId);

  const primaryAhu = airHandlerUnit[0];

  const form = useAppForm({
    defaultValues: {
      command: primaryAhu?.freeCooling?.command ?? false,
      delta: primaryAhu?.freeCooling?.delta ?? DEFAULT_FREE_COOLING_DELTA,
    },
    onSubmit: async ({ formApi, value }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirty) return;
      const freeCooling = {
        ...(dirty.command !== undefined && { command: value.command }),
        ...(dirty.delta !== undefined && { delta: value.delta }),
      };
      // Group settings apply to every unit of the group, like the legacy bulk PATCH.
      // Each failed PATCH raises its own error dialog; keep the form dirty unless every unit succeeded.
      const results = await Promise.allSettled(
        airHandlerUnit.map(ahu =>
          mutateAsync({
            airHandlerUnitId: ahu.id,
            body: { freeCooling },
          }),
        ),
      );
      if (results.every(result => result.status === "fulfilled")) formApi.reset(value);
    },
  });

  const delta = useSelector(form.store, state => state.values.delta);

  return (
    <form
      className="max-w-105 gap-2.5 flex h-full flex-col"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <Heading className="text-base text-primary-blue" as="h3">
          {m.free_cooling()}
        </Heading>
        <Alert severity="help">{m.free_cooling_help()}</Alert>
        <div className="gap-2.5 flex items-center">
          <form.AppField name="command">
            {field => (
              <div className="flex-1">
                <field.Switch />
              </div>
            )}
          </form.AppField>
          <Inconsistency property="freeCooling.command" />
        </div>
        <form.AppField name="delta">
          {field => (
            <field.Slider
              label={<DiscrepancyLabel label={`${m.temperature_delta_setpoint()} (°C)`} property="freeCooling.delta" />}
              min={MINIMUM_FREE_COOLING_DELTA}
              max={MAXIMUM_FREE_COOLING_DELTA}
              step={STEP}
            />
          )}
        </form.AppField>
        <p className="text-base [&_span]:italic">
          <ParaglideMessage
            message={m.free_cooling_text}
            inputs={{ temperature: delta }}
            markup={{
              span: ({ children }) => <span>{children}</span>,
            }}
          />
        </p>
        <form.SubmitButton className="mt-auto self-center" label={m.save()} />
      </form.AppForm>
    </form>
  );
}
